import { useNavigator } from '@repo/toolkit/use-navigator'
import { Column } from '@repo/ui/data-table'
import { TableDate } from '@repo/ui/data-table-cells'
import { PlusIcon, SettingIcon } from '@repo/ui/icons'
import { useQuery } from '@tanstack/react-query'
import { useRef } from 'react'

import { API } from '../../../api/apis.config'
import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import CountryFlag from '../../../components/elements/country-flag'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import { Paths } from '../../../lib/constants/path'
import { insuranceTypeEnumMap } from '../../../lib/types/enums/insurance/insurance-type.enum'
import { Insurance } from '../../../lib/types/models/insurance/insurance'

export function InsuranceListPage() {
  const { navigate, navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<Insurance>>(null)

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const columns: Column<Insurance>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true
    },
    {
      accessorKey: 'price',
      header: 'Prix',
      enableSorting: true
    },
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true
    },
    {
      accessorKey: 'type',
      header: 'Type',
      enableSorting: true,
      cell: ({ row }) => insuranceTypeEnumMap[row.original.type]
    },
    {
      accessorKey: 'elligibleResidenceCountries',
      header: 'Pays de résidence',
      cell: ({ row }) => {
        if (!countryList) return '-'
        const countryNames = row.original.elligibleResidenceCountries

        return (
          <div className={'flex flex-wrap gap-2 py-3'}>
            {countryNames.map((code) => (
              <CountryFlag key={code} code={code} />
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'elligibleDestinationCountries',
      header: 'Pays Eligibles',
      cell: ({ row }) => {
        if (!countryList) return '-'
        const countries = row.original.elligibleDestinationCountries

        return (
          <div className={'flex flex-wrap gap-2 py-3'}>
            {countries.map(({ code }) => (
              <CountryFlag key={code} code={code} />
            ))}
          </div>
        )
      }
    },

    {
      accessorKey: 'updatedAt',
      header: 'Date de modification',
      cell: ({ row }) => <TableDate date={row.original.updatedAt} />
    }
  ]

  return (
    <div>
      <UserNavBar title={'Assurances'} icon={SettingIcon} />
      <div className={'h-4'} />
      <DynamicDataTable<Insurance>
        ref={tableRef}
        title={'Assurances'}
        columns={columns}
        deleteMessage={{ name: 'assurance', attributeName: 'name' }}
        selectable
        onRowClick={({ id }, event) =>
          navigateWithEvent(Paths.INSURANCE_UPDATE_PAGE.replace(':id', id.toString()), event)
        }
        allowSearchParam
        endpoint={API.insurances.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer une assurance',
            fn: () => navigate(Paths.INSURANCE_CREATE_PAGE)
          }
        ]}
      />
    </div>
  )
}
